import {
  useClient,
  useResetPasswordSuccess,
  useScreen,
  useTenant,
} from "@auth0/auth0-acul-react/reset-password-success";

import StatusScreen from "@/components/universal-login/StatusScreen";
import {
  resolveBackHref,
  resolveBackLabel,
} from "@/utils/helpers/backToApplication";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";

import locales from "./locales/en.json";

/**
 * reset-password-success: the terminal screen of EVERY password-reset journey
 * on the tenant (RO-A activation, the in-portal reset, and the login page's
 * "Forgot password?" code journey). The renderer is tenant-wide; there's no
 * client filter.
 *
 * A like-for-like replica of the hosted screen, not a redesign. Copy comes from
 * Auth0's own screen.texts when present (so custom text and locales behave
 * as they do on the hosted page), else from the strings captured off the live
 * page in locales/en.json.
 *
 * Deliberately calls ONLY applyAuth0Theme(): no applyCareSuperBrand() (the
 * signup journey's indigo) and no applyMemberPortalBrand(). The hosted reset
 * screens use the tenant theme, so the replica must too.
 */
function ResetPasswordSuccessScreen() {
  const resetPasswordSuccess = useResetPasswordSuccess();
  const screen = useScreen();
  const client = useClient();
  const tenant = useTenant();

  applyAuth0Theme(resetPasswordSuccess);

  const texts = (screen.texts || {}) as Record<string, string | undefined>;
  // Only when Auth0 supplies one: the hosted page's <title> was EMPTY when
  // captured live, so inventing one here would be a visible difference (the
  // browser tab).
  if (texts.pageTitle) document.title = texts.pageTitle;

  return (
    <StatusScreen
      title={texts.eventTitle || texts.title || locales.title}
      description={texts.description || locales.description}
      backLabel={resolveBackLabel(
        client?.name,
        tenant?.friendlyName,
        locales.backToApp
      )}
      backHref={resolveBackHref(screen.links)}
    />
  );
}

export default ResetPasswordSuccessScreen;
